import React, { useState, useMemo } from 'react';
import { getAssumeWeCanResourceItems } from '../../data/assumeWeCanResources';
import { getEmployerResourceItems } from '../../data/employerResources';
import { getParentResourceItems } from '../../data/parentResources';
import { getCommunityResourceItems } from '../../data/communityResources';
import { getEducatorResourceItems } from '../../data/educatorResources';
import ckLogo from '../../CK-logo.png';
import './Resources.css';

const CAMPFIRE_KINSHIP_RESOURCES_URL = 'https://campfirekinship.com/resources/';

const assumeWeCanResourceItems = getAssumeWeCanResourceItems();
const educatorResourceItems = getEducatorResourceItems();
const communityResourceItems = getCommunityResourceItems();
const parentResourceItems = getParentResourceItems();

/**
 * Audience-aligned resource buckets - colors match LMS accents.
 * Files live under public/resources/{assumewecan|employers|parents|community|educators}/ - see src/data/*Resources.js and folderResources.js.
 */
const AUDIENCE_SECTIONS = [
  {
    id: 'assume-we-can',
    title: 'Assume We Can',
    subtitle:
      'Project materials from Assume We Can - learning and research resources supporting South Asian women in STEMM.',
    accent: 'assume',
    items:
      assumeWeCanResourceItems.length > 0
        ? assumeWeCanResourceItems
        : [
            {
              id: 'awc-placeholder',
              title: 'Assume We Can resources',
              description:
                'Add PDFs under public/resources/assumewecan/ and list them in src/data/assumeWeCanResources.js.',
              href: null,
              icon: '📄',
            },
          ],
  },
  {
    id: 'south-asian-women',
    title: 'Resources for South Asian Women',
    subtitle:
      'Materials grounded in the Breaking Barriers research - stories, reflections, and tools centered on your experience in STEMM.',
    accent: 'women',
    items: [
      {
        id: 'saw-executive-summary',
        title: 'Executive summary (PDF)',
        description: 'High-level overview of key findings from the national research - coming soon as a downloadable PDF.',
        href: null,
        icon: '📄',
      },
      {
        id: 'saw-workbook',
        title: 'Reflection workbook',
        description: 'Space to pause, reflect, and track what you notice in your own context - companion to the learning series.',
        href: null,
        icon: '📝',
      },
    ],
  },
  {
    id: 'employers',
    title: 'Resources for Employers',
    subtitle:
      'For leaders shaping hiring, evaluation, and work design - toolkits, infosheets, and guides from partner organizations.',
    accent: 'employers',
    items: getEmployerResourceItems(),
  },
  {
    id: 'educators',
    title: 'Resources for Educators',
    subtitle:
      'Support inclusive STEMM learning and facilitation - dialogue tools and printable guides.',
    accent: 'educators',
    items:
      educatorResourceItems.length > 0
        ? educatorResourceItems
        : [
            {
              id: 'edu-discussion',
              title: 'Discussion prompts',
              description:
                'Add PDFs or DOCX under public/resources/educators/ and list them in src/data/educatorResources.js.',
              href: null,
              icon: '💬',
            },
          ],
  },
  {
    id: 'community-leaders',
    title: 'Resources for Community Leaders',
    subtitle:
      'Build collective impact and connections - tools for organizers, networks, and community-facing roles.',
    accent: 'community',
    items:
      communityResourceItems.length > 0
        ? communityResourceItems
        : [
            {
              id: 'com-hosting',
              title: 'Community resources',
              description:
                'Add files under public/resources/community/ and list them in src/data/communityResources.js.',
              href: null,
              icon: '📅',
            },
          ],
  },
  {
    id: 'parents',
    title: 'Resources for Parents',
    subtitle:
      'Guidance for supporting the next generation in STEMM - family-facing tips and reflections.',
    accent: 'parents',
    items:
      parentResourceItems.length > 0
        ? parentResourceItems
        : [
            {
              id: 'par-home',
              title: 'Parent resources',
              description:
                'Add files under public/resources/parents/ and list them in src/data/parentResources.js.',
              href: null,
              icon: '🏠',
            },
          ],
  },
];

const FILTER_ALL = 'all';

const Resources = () => {
  const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
  const [searchQuery, setSearchQuery] = useState('');

  const flatItems = useMemo(
    () =>
      AUDIENCE_SECTIONS.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          audienceId: section.id,
          audienceTitle: section.title,
          sectionAccent: section.accent,
        }))
      ),
    []
  );

  const totalResourceCount = flatItems.length;
  const comingSoonCount = flatItems.filter((i) => !i.href).length;

  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return AUDIENCE_SECTIONS.map((section) => {
      if (activeFilter !== FILTER_ALL && section.id !== activeFilter) {
        return { ...section, items: [] };
      }
      const items = section.items.filter((item) => {
        if (!q) return true;
        return (
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q) ||
          section.title.toLowerCase().includes(q)
        );
      });
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [activeFilter, searchQuery]);

  const filterOptions = [
    { id: FILTER_ALL, label: 'All resources' },
    { id: 'assume-we-can', label: 'Assume We Can' },
    { id: 'south-asian-women', label: 'South Asian Women' },
    { id: 'employers', label: 'Employers' },
    { id: 'educators', label: 'Educators' },
    { id: 'community-leaders', label: 'Community Leaders' },
    { id: 'parents', label: 'Parents' },
  ];

  return (
    <div className="resources-page">
      <div className="resources-container">
        <header className="resources-header">
          <div className="header-top">
            <div>
              <h1 className="page-title">Resources</h1>
              <p className="page-subtitle">
                Curated materials by audience - aligned with the Breaking Barriers learning series and research.
              </p>
            </div>
            <div className="header-search">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="search"
                placeholder="Search resources..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search resources"
              />
            </div>
          </div>

          <div className="resources-stats">
            <div className="stat-item">
              <span className="stat-number">{totalResourceCount}</span>
              <span className="stat-text">Resources</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{comingSoonCount}</span>
              <span className="stat-text">Coming soon</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{AUDIENCE_SECTIONS.length}</span>
              <span className="stat-text">Audiences</span>
            </div>
          </div>
        </header>

        <nav className="resources-filter-bar" aria-label="Filter by audience">
          {filterOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`resources-filter-pill ${activeFilter === opt.id ? 'active' : ''} resources-filter-pill--${opt.id === FILTER_ALL ? 'all' : opt.id}`}
              onClick={() => setActiveFilter(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </nav>

        {activeFilter === FILTER_ALL && (
          <section
            className="resources-campfire-highlight"
            aria-labelledby="resources-campfire-heading"
          >
            <div className="resources-campfire-highlight-inner">
              <div className="resources-campfire-highlight-brand">
                <img
                  src={ckLogo}
                  alt=""
                  className="resources-campfire-logo"
                  aria-hidden="true"
                />
                <span className="resources-campfire-partner-label">Campfire Kinship</span>
              </div>
              <div className="resources-campfire-highlight-content">
                <h2 id="resources-campfire-heading" className="resources-campfire-title">
                  Explore Campfire Kinship Resources
                </h2>
                <p className="resources-campfire-desc">
                  {/* Visit the Campfire Kinship resources hub for story guides, newsletters, Assume We Can project learning materials, e-learning courses, podcasts, and more. */}
                </p>
                <a
                  href={CAMPFIRE_KINSHIP_RESOURCES_URL}
                  className="resources-campfire-cta"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit Campfire Kinship Resources
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </section>
        )}

        <div className="resources-sections-wrap">
          {filteredSections.length === 0 ? (
            <div className="resources-empty">
              <p className="resources-empty-title">No resources match your search</p>
              <p className="resources-empty-desc">Try another keyword or choose &ldquo;All resources&rdquo;.</p>
            </div>
          ) : (
            filteredSections.map((section) => (
              <section
                key={section.id}
                className={`resources-audience-section resources-audience-section--${section.accent}`}
                aria-labelledby={`resources-heading-${section.id}`}
              >
                <div className="resources-audience-section-head">
                  <h2 id={`resources-heading-${section.id}`} className="resources-audience-title">
                    {section.title}
                  </h2>
                  <p className="resources-audience-subtitle">{section.subtitle}</p>
                </div>

                <ul className="resources-audience-grid">
                  {section.items.map((item) => {
                    // Source tagging: external by default for linked resources; override with item.sourceTag (e.g., "LS Team").
                    const sourceTag = item.sourceTag || (item.href ? 'External' : null);
                    return (
                    <li key={item.id}>
                      <article className={`resources-item-card resources-item-card--${section.accent}`}>
                        <div className="resources-item-card-top">
                          <span className="resources-item-icon" aria-hidden="true">
                            {item.icon}
                          </span>
                          <span className="resources-item-kind">Download / guide</span>
                        </div>
                        <h3 className="resources-item-title">{item.title}</h3>
                        <p className="resources-item-desc">{item.description}</p>
                        <div className="resources-item-footer">
                          {item.href ? (
                            <div className="resources-item-footer-row">
                              <a
                                href={item.href}
                                className="resources-item-cta resources-item-cta--secondary"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                                Read Resource
                              </a>
                              {sourceTag ? (
                                <span className={`resources-item-source-tag resources-item-source-tag--${sourceTag === 'LS Team' ? 'team' : 'external'}`}>
                                  {sourceTag}
                                </span>
                              ) : null}
                            </div>
                          ) : (
                            <span className="resources-item-soon">Coming soon</span>
                          )}
                        </div>
                      </article>
                    </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Resources;
